import * as yup from 'yup'
import { IUser, USER_TYPES } from 'apps/user/types/user'
import { validatePhone } from 'utils/validators'

const baseUserSchema = {
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  role: yup.string().required('User role is required'),
  userType: yup.string().required('User type is required'),
}

const emailSchema = {
  email: yup.string().required('Email address is required').email('Use a valid email address'),
}

const cellPhoneSchema = {
  cellPhone: yup
    .string()
    .required('Cell phone number is required')
    .test('isValidPhoneNumber', 'Please enter a valid cell phone number', validatePhone)
    .nullable(),
}

const phoneNumberSchema = {
  phone: yup
    .string()
    .required('Phone number is required')
    .test('isValidPhoneNumber', 'Please enter a valid phone number', validatePhone)
    .nullable(),
  phoneExt: yup.string().nullable(),
}

const adminUserSchema = {
  ...baseUserSchema,
  ...cellPhoneSchema,
  ...emailSchema,
  ...phoneNumberSchema,
}

const shipperUserSchema = {
  ...baseUserSchema,
  ...emailSchema,
  ...phoneNumberSchema,
  applicationId: yup.string().required('Select a shipper organization'),
  applicationType: yup
    .string()
    .required('Select a shipper organization')
    .matches(/(Shipper|Carrier)Application/),
}

const carrierOrgSchema = {
  applicationId: yup.string().required('Select a carrier organization'),
  applicationType: yup
    .string()
    .required('Select a carrier organization')
    .matches(/(Shipper|Carrier)Application/),
}

const carrierUserSchema = {
  ...baseUserSchema,
  ...cellPhoneSchema,
  ...emailSchema,
  ...phoneNumberSchema,
  ...carrierOrgSchema,
}

const driverUserSchema = {
  ...baseUserSchema,
  ...cellPhoneSchema,
  ...carrierOrgSchema,
}

const dispatcherUserSchema = {
  ...carrierUserSchema,
  cellPhone: yup
    .string()
    .test('isValidPhoneNumber', 'Please enter a valid cell phone number', value => {
      if (!value) return true
      return String(value).replace(/\D/g, '').length === 10
    })
    .nullable(),
}

const setSchema = (userType: USER_TYPES) => {
  let schema

  // select validation schema
  switch (userType) {
    case USER_TYPES.driver:
      schema = driverUserSchema
      break
    case USER_TYPES.dispatcher:
      schema = dispatcherUserSchema
      break
    case USER_TYPES.shipper:
      schema = shipperUserSchema
      break
    case USER_TYPES.admin:
      schema = adminUserSchema
      break
    default:
      schema = carrierUserSchema
  }

  return schema
}

const validateUser = (parentUser: Partial<IUser>) => {
  const schema: { [key: string]: yup.StringSchema<string | null> } = setSchema(
    parentUser.userType as USER_TYPES
  )
  let validationMessages

  try {
    return yup.object().shape(schema).validateSync(parentUser, { abortEarly: false })
  } catch (errs) {
    validationMessages = errs.inner.reduce(
      (acc: { [key: string]: string }, { message, path }: { path: string; message: string }) => {
        acc[path] = message
        return acc
      },
      {}
    )

    throw validationMessages
  }
}

export const validateChildUser = validateUser
export const validateParentUser = validateUser
